import AdStatusButtons from '../../components/AdStatusButtons';
import AdStatusToggle from '../../components/AdStatusToggle';
import { useAdStatus, useMayChangeStatus, NOUN } from '../../components/ad-status';
import { resolveSelectedIds } from '../../utils/entity-list';
import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  ContentCopy,
  Delete,
} from '@mui/icons-material';
import {
  DataGridPremium,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adsetsService, AdSet } from '../../services/adsets.service';
import { campaignsService } from '../../services/campaigns.service';
import StatusBadge from '../../components/StatusBadge';
import EntityListFilters, { type EntityFilters } from '../../components/EntityListFilters';

export default function AdSetsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [filters, setFilters] = useState<EntityFilters>({ search: '', status: '', adAccountId: '' });
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set<string>() });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAdSet, setMenuAdSet] = useState<AdSet | null>(null);
  const [dupDialogOpen, setDupDialogOpen] = useState(false);
  const [dupTargetCampaign, setDupTargetCampaign] = useState('');

  const campaignId = searchParams.get('campaignId') || '';

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignsService.query({ limit: 100 }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['adsets', campaignId, page, pageSize, filters],
    queryFn: () => adsetsService.query({
      campaign_id: campaignId || undefined,
      offset: page * pageSize,
      limit: pageSize,
      ...(filters.search && { search: filters.search }),
      ...(filters.status && { status: filters.status }),
      ...(filters.adAccountId && { ad_account_id: filters.adAccountId }),
    }),
  });

  const handleFiltersChange = (next: EntityFilters) => {
    setFilters(next);
    setPage(0);
  };

  const deleteMutation = useMutation({
    mutationFn: adsetsService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adsets'] });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: ({ id, campaign_id }: { id: string; campaign_id?: string }) =>
      adsetsService.duplicate(id, { campaign_id }),
    onSuccess: (newAdSet) => {
      queryClient.invalidateQueries({ queryKey: ['adsets'] });
      setDupDialogOpen(false);
      navigate(`/adsets/${newAdSet.id}/edit`);
    },
  });

  const mayChangeStatus = useMayChangeStatus();
  const rowStatus = useAdStatus('adset', 'adsets');

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Ad Set Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'campaign',
      headerName: 'Campaign',
      width: 180,
      valueGetter: (_value, row) => row.campaign?.name || '-',
    },
    {
      field: 'ad_account',
      headerName: 'Ad Account',
      width: 180,
      valueGetter: (_value, row) => row.ad_account?.name || '-',
    },
    {
      field: 'fb_adset_id',
      headerName: 'FB ID',
      width: 160,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return <Typography variant="caption" color="text.disabled">-</Typography>;
        return (
          <Tooltip title="Click to copy">
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', fontSize: '0.7rem', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
              onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(params.value); }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <StatusBadge
            status={params.value}
            effectiveStatus={params.row.effective_status}
            staleSince={params.row.status_stale_since}
          />
          {mayChangeStatus && (
            <AdStatusToggle
              status={params.value}
              noun={NOUN['adset'][0]}
              onToggle={(next) => rowStatus.ask([String(params.row.id)], next)}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'optimization_goal',
      headerName: 'Optimization',
      width: 150,
    },
    {
      field: 'billing_event',
      headerName: 'Billing',
      width: 120,
    },
    {
      field: 'daily_budget',
      headerName: 'Daily Budget',
      width: 130,
      valueFormatter: (value) =>
        value ? `$${Number(value).toFixed(2)}` : '-',
    },
    {
      field: 'lifetime_budget',
      headerName: 'Lifetime Budget',
      width: 140,
      valueFormatter: (value) =>
        value ? `$${Number(value).toFixed(2)}` : '-',
    },
    {
      field: 'bid_amount',
      headerName: 'Bid',
      width: 100,
      valueFormatter: (value) =>
        value ? `$${Number(value).toFixed(2)}` : 'Auto',
    },
    {
      field: 'start_time',
      headerName: 'Start',
      width: 120,
      valueFormatter: (value) =>
        value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      field: 'end_time',
      headerName: 'End',
      width: 120,
      valueFormatter: (value) =>
        value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (value) =>
        new Date(value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
            setMenuAdSet(params.row);
          }}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  const rows = data?.data || [];
  const selectedIds = resolveSelectedIds(selectedRows, rows);
  const totalsRow = useMemo(() => {
    if (rows.length === 0) return null;
    return {
      id: '__totals__',
      name: `Total (${data?.total || rows.length})`,
      campaign: null,
      ad_account: null,
      fb_adset_id: null,
      status: '',
      optimization_goal: '',
      billing_event: '',
      daily_budget: rows.reduce((s, r) => s + (r.daily_budget || 0), 0) || null,
      lifetime_budget: rows.reduce((s, r) => s + (r.lifetime_budget || 0), 0) || null,
      bid_amount: null,
      start_time: '',
      end_time: '',
      created_at: '',
    };
  }, [rows, data?.total]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Ad Sets
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedIds.length > 0 && (
            <AdStatusButtons
              entityType="adset"
              ids={selectedIds}
              invalidateKey="adsets"
              alreadyInState={(status) => selectedIds.filter(
                (id) => rows.find((r) => String(r.id) === id)?.status === status).length}
              onDone={() => setSelectedRows({ type: 'include', ids: new Set() })}
            />
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/adsets/new')}
          >
            Create Ad Set
          </Button>
        </Box>
      </Box>

      <EntityListFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        extraFilters={
          <Autocomplete
            size="small"
            sx={{ minWidth: 250 }}
            options={campaigns?.data || []}
            getOptionLabel={(o) => o.name}
            value={campaigns?.data?.find(c => c.id === campaignId) || null}
            onChange={(_, v) => {
              if (v) {
                setSearchParams({ campaignId: v.id });
              } else {
                setSearchParams({});
              }
              setPage(0);
            }}
            renderInput={(params) => <TextField {...params} label="Campaign" placeholder="All campaigns" />}
          />
        }
      />

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isLoading}
        rowCount={data?.total || 0}
        pagination
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model: GridPaginationModel) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        pageSizeOptions={[50, 100, 200, 400]}
        checkboxSelection
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={setSelectedRows}
        disableRowSelectionOnClick
        pinnedRows={totalsRow ? { top: [totalsRow] } : undefined}
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          height: 'calc(100vh - 280px)',
          minHeight: 400,
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          '& .MuiDataGrid-pinnedRows': {
            bgcolor: 'action.hover',
            fontWeight: 600,
          },
        }}
        onRowClick={(params: GridRowParams) => {
          if (params.id === '__totals__') return;
          navigate(`/adsets/${params.id}`);
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            navigate(`/adsets/${menuAdSet?.id}/edit`);
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAdSet) {
              setDupTargetCampaign(menuAdSet.campaign_id);
              setDupDialogOpen(true);
            }
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAdSet) {
              deleteMutation.mutate(menuAdSet.id);
            }
            setAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={dupDialogOpen} onClose={() => setDupDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Duplicate Ad Set</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {menuAdSet?.name}
          </Typography>
          <Autocomplete
            fullWidth
            size="small"
            options={campaigns?.data || []}
            getOptionLabel={(o) => `${o.name}${o.id === menuAdSet?.campaign_id ? ' (current)' : ''}`}
            value={(campaigns?.data || []).find(c => c.id === dupTargetCampaign) || null}
            onChange={(_, v) => setDupTargetCampaign(v?.id || '')}
            renderInput={(params) => <TextField {...params} label="Target Campaign" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDupDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!dupTargetCampaign || duplicateMutation.isPending}
            onClick={() => {
              if (menuAdSet) {
                duplicateMutation.mutate({
                  id: menuAdSet.id,
                  campaign_id: dupTargetCampaign !== menuAdSet.campaign_id ? dupTargetCampaign : undefined,
                });
              }
            }}
          >
            {duplicateMutation.isPending ? 'Duplicating...' : 'Duplicate'}
          </Button>
        </DialogActions>
      </Dialog>

      {rowStatus.dialogs}
    </Box>
  );
}
